import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { VerseBlock } from "@/components/VerseBlock";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export function Favorites() {
  const [verses, setVerses] = useState([]);
  useEffect(() => { api.get("/favorites").then((r) => setVerses(r.data)); }, []);
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
      <h1 className="font-serif-display text-4xl text-[#0F172A] mb-2">Favorite Proverbs</h1>
      <p className="text-[#475569] mb-8">Verses you've saved to return to.</p>
      {verses.length === 0 ? <Empty text="No favorites yet. Tap the heart on any verse to save it." /> :
        <div className="space-y-5">{verses.map((v, i) => <VerseBlock key={v.id} v={v} index={i} />)}</div>}
    </div>
  );
}

export function Journal() {
  const [items, setItems] = useState([]);
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const load = () => api.get("/journal").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!content.trim()) { toast.error("Write something first."); return; }
    try { await api.post("/journal", { title, content }); setContent(""); setTitle(""); load(); toast.success("Journal entry saved."); }
    catch { toast.error("Could not save."); }
  };
  const del = async (id) => { await api.delete(`/journal/${id}`); load(); };
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
      <h1 className="font-serif-display text-4xl text-[#0F172A] mb-2">Private Journal</h1>
      <p className="text-[#475569] mb-8">Your reflections are private and visible only to you.</p>
      <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 mb-8">
        <input data-testid="journal-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)"
          className="w-full rounded-lg border border-[#E2E8F0] px-4 py-2.5 mb-3 focus:outline-none focus:border-[#2C4C3B]" />
        <textarea data-testid="journal-content-input" value={content} onChange={(e) => setContent(e.target.value)} rows={4} placeholder="What is on your heart today?"
          className="w-full rounded-lg border border-[#E2E8F0] px-4 py-3 resize-none focus:outline-none focus:border-[#2C4C3B]" />
        <button data-testid="journal-save-btn" onClick={add} className="mt-3 px-6 py-2.5 rounded-full bg-[#2C4C3B] text-white font-medium">Save entry</button>
      </div>
      {items.length === 0 ? <Empty text="No journal entries yet." /> :
        <div className="space-y-4">
          {items.map((it) => (
            <div key={it.id} data-testid={`journal-entry-${it.id}`} className="rounded-xl border border-[#E2E8F0] bg-white p-5">
              <div className="flex items-start justify-between">
                <div>
                  {it.title && <h3 className="font-serif-display text-xl text-[#0F172A]">{it.title}</h3>}
                  <p className="text-xs text-[#94A3B8]">{new Date(it.at).toLocaleString()}</p>
                </div>
                <button onClick={() => del(it.id)} className="text-[#94A3B8] hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
              <p className="text-[15px] text-[#334155] leading-relaxed mt-2 whitespace-pre-wrap">{it.content}</p>
            </div>
          ))}
        </div>}
    </div>
  );
}

function Empty({ text }) {
  return <div className="rounded-2xl border border-dashed border-[#E2E8F0] bg-white/50 px-6 py-14 text-center text-[#94A3B8]">{text}</div>;
}

export function SavedWisdom() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/saved-wisdom").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const del = async (id) => { await api.delete(`/saved-wisdom/${id}`); load(); toast.success("Removed."); };
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
      <h1 className="font-serif-display text-4xl text-[#0F172A] mb-2">My Saved Wisdom</h1>
      <p className="text-[#475569] mb-8">Scriptures you've saved to return to.</p>
      {items.length === 0 ? <Empty text="No saved wisdom yet. Tap “Save” on any Scripture to keep it here." /> :
        <div className="space-y-4">
          {items.map((it) => (
            <div key={it.id} data-testid={`saved-wisdom-${it.id}`} className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
              <div className="flex items-start justify-between gap-3">
                <div className="text-xs uppercase tracking-widest text-[#B8912F] font-semibold">{it.reference} · {it.translation}</div>
                <button data-testid={`delete-saved-${it.id}`} onClick={() => del(it.id)} className="text-[#94A3B8] hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="mt-3 bg-[#F9F7F1] border-l-2 border-[#B8912F] rounded-r-lg px-5 py-4">
                <p className="font-serif-display italic text-xl leading-relaxed text-[#0F172A]">"{it.text}"</p>
              </div>
              {it.explanation && <p className="text-[15px] text-[#334155] leading-relaxed mt-3">{it.explanation}</p>}
              {it.topic && <p className="text-xs text-[#94A3B8] mt-2">Saved under {it.topic} · {new Date(it.saved_at).toLocaleDateString()}</p>}
            </div>
          ))}
        </div>}
    </div>
  );
}

